import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import AuthForm from "@/polymet/components/auth-form";
import OTPVerificationForm from "@/polymet/components/otp-verification-form";
import BreadcrumbNavigation from "@/polymet/components/breadcrumb-navigation";
import { sendOTP, verifyOTP, createUserProfileIfNotExists } from "@/lib/supabase";
import { useAuth } from "@/lib/auth-context";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, refreshUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showOTPVerification, setShowOTPVerification] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [error, setError] = useState("");
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  
  // Get redirect information from location state
  const redirectPath = location.state?.from || '/';
  const redirectMessage = location.state?.message;
  
  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectPath);
    }
  }, [isAuthenticated, navigate, redirectPath]);

  // Handle login form submission
  const handleLoginSubmit = async (data: { phone: string }) => {
    setIsLoading(true);
    setError("");

    try {
      const result = await sendOTP(data.phone);
      
      if (result.success) {
        setPhoneNumber(data.phone);
        setShowOTPVerification(true);
      } else {
        console.error('Failed to send OTP:', result);
        setError(result.error || "Failed to send verification code. Please try again.");
      }
    } catch (err: any) {
      console.error("Error sending OTP:", err);
      setError(`An unexpected error occurred: ${err?.message || 'Unknown error'}. Please try again.`);
    }
    
    setIsLoading(false);
  };

  // Handle OTP verification
  const handleVerifyOTP = async (otp: string) => {
    setIsLoading(true);
    setError("");

    try {
      // Verify the OTP
      const result = await verifyOTP(phoneNumber, otp);
      
      if (result.success && result.data?.user) {
        // Create user profile if it doesn't exist
        const userId = result.data.user.id;
        
        try {
          const profileResult = await createUserProfileIfNotExists(userId, phoneNumber);
          
          if (profileResult.success) {
            setVerificationSuccess(true);
            
            // Update auth state
            await refreshUser();
            
            // Redirect to profile setup immediately if it's a new profile
            if (profileResult.isNew) {
              navigate('/account-setup');
            } else {
              // If profile already exists, redirect to intended page or home
              navigate(redirectPath);
            }
          } else {
            console.error('Profile creation failed:', profileResult);
            setError(
              `Failed to create user profile: ${profileResult.error}. ` +
              `${profileResult.errorCode ? `Code: ${profileResult.errorCode}. ` : ''}` +
              `${profileResult.errorDetails ? `Details: ${profileResult.errorDetails}` : ''}`
            );
          }
        } catch (profileErr: any) {
          console.error('Exception during profile creation:', profileErr);
          setError(`Exception during profile creation: ${profileErr?.message || 'Unknown error'}`);
        }
      } else {
        console.error('OTP verification failed:', result);
        setError(result.error || "Invalid verification code. Please try again.");
      }
    } catch (err: any) {
      console.error("Error verifying OTP:", err);
      setError(`An unexpected error occurred: ${err?.message || 'Unknown error'}. Please try again.`);
    }
    
    setIsLoading(false);
  };

  // Handle resend OTP
  const handleResendOTP = async () => {
    setIsLoading(true);
    setError("");
    
    try {
      const result = await sendOTP(phoneNumber);
      
      if (result.success) {
        // Success - no need to log
      } else {
        console.error('Failed to resend OTP:', result);
        setError(result.error || "Failed to resend verification code. Please try again.");
      }
    } catch (err: any) {
      console.error("Error resending OTP:", err);
      setError(`An unexpected error occurred: ${err?.message || 'Unknown error'}. Please try again.`);
    }
    
    setIsLoading(false);
  };

  // Go back to login form
  const handleBackToLogin = () => {
    setShowOTPVerification(false);
    setError("");
  };

  // Breadcrumb items
  const breadcrumbItems = [
    { name: "Account", href: "/account" },
    { name: "Login", href: "/login" }
  ];

  return (
    <div className="container max-w-screen-xl mx-auto px-4 py-12">
      {/* Breadcrumb Navigation */}
      <BreadcrumbNavigation items={breadcrumbItems} />
      
      <div className="flex flex-col items-center justify-center min-h-[70vh]">
        <div className="w-full max-w-md mb-8">
          <h1 className="text-3xl font-bold text-center mb-2">Welcome Back</h1>
          <p className="text-center text-muted-foreground mb-8">
            Login to access your MachineryMarket account
          </p>

          {/* Show redirect message if provided */}
          {redirectMessage && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-blue-800 text-sm text-center">{redirectMessage}</p>
            </div>
          )}

          {showOTPVerification ? (
            verificationSuccess ? (
              <div className="flex flex-col items-center space-y-6">
                <div className="bg-green-100 dark:bg-green-900 p-4 rounded-md text-center">
                  <h3 className="text-lg font-medium text-green-800 dark:text-green-200 mb-2">
                    Login Successful!
                  </h3>
                  <p className="text-green-700 dark:text-green-300">
                    You have successfully logged in to your account.
                  </p>
                </div>
                <Link
                  to={redirectPath}
                  className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2 w-full"
                >
                  {redirectPath === '/' ? 'Go to Homepage' : 'Continue'}
                </Link>
              </div>
            ) : (
              <OTPVerificationForm
                contactMethod={{ type: "phone", value: phoneNumber }}
                onVerify={handleVerifyOTP}
                onResend={handleResendOTP}
                onBack={handleBackToLogin}
                isLoading={isLoading}
                error={error}
              />
            )
          ) : (
            <AuthForm
              mode="login"
              onSubmit={handleLoginSubmit}
              isLoading={isLoading}
            />
          )}
        </div>
      </div>
    </div>
  );
}
